import { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Check, Building2, Settings, Search, LayoutDashboard } from 'lucide-react';
import { useProperty } from '../../contexts/PropertyContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { API_CONFIG } from '../../config/api';
import type { OrganizationsListResponse } from '@lasoxp/contracts';
import type { Property } from '../../types';
import { Button, Input } from '../shared';

// ---------------------------------------------------------------------------
// Org-level role detection
// ---------------------------------------------------------------------------

const ORG_LEVEL_ROLES = new Set([
  'org_admin', 'regional_director', 'regional_revenue_manager',
  'regional_marketing_director', 'corporate_ops_director', 'asset_manager',
  'portfolio_owner',
]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrgGroup {
  orgId: string;
  orgName: string;
  properties: Property[];
  hasOrgAccess: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PropertySwitcher() {
  const { selectedProperty, setSelectedProperty, availableProperties, isSuperAdmin } = useProperty();
  const { adminUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [orgNames, setOrgNames] = useState<Record<string, string>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const hasOrgRole = adminUser ? ORG_LEVEL_ROLES.has(adminUser.primaryRole) : false;
  const accessibleOrgIds = useMemo(
    () => adminUser?.accessibleOrganizationIds ?? [],
    [adminUser?.accessibleOrganizationIds],
  );

  // Fetch org names when properties are available
  useEffect(() => {
    const orgIds = [...new Set(availableProperties.map(p => p.organizationId).filter(Boolean))];
    if (orgIds.length === 0) return;

    // Only fetch if we have org IDs we don't know names for yet
    const unknownOrgIds = orgIds.filter(id => !orgNames[id]);
    if (unknownOrgIds.length === 0) return;

    apiService.get<OrganizationsListResponse>(`${API_CONFIG.endpoints.organizations.list}?limit=200`)
      .then(data => {
        const names: Record<string, string> = {};
        for (const org of data.organizations) {
          names[org.id] = org.name;
        }
        setOrgNames(prev => ({ ...prev, ...names }));
      })
      .catch(() => {
        // Silently fail — we'll just show org IDs as fallback
      });
  // Only re-fetch when the set of org IDs changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableProperties.map(p => p.organizationId).join(',')]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter properties based on search query
  const filteredProperties = useMemo(() =>
    availableProperties.filter(property => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        property.name.toLowerCase().includes(query) ||
        property.website?.toLowerCase().includes(query) ||
        property.id.toLowerCase().includes(query)
      );
    }),
  [availableProperties, searchQuery]);

  // Group filtered properties by organization
  const orgGroups = useMemo((): OrgGroup[] => {
    const groupMap = new Map<string, OrgGroup>();

    for (const property of filteredProperties) {
      const orgId = property.organizationId || '__ungrouped__';
      if (!groupMap.has(orgId)) {
        groupMap.set(orgId, {
          orgId,
          orgName: orgId === '__ungrouped__' ? 'Ungrouped' : (orgNames[orgId] ?? orgId),
          properties: [],
          hasOrgAccess: accessibleOrgIds.includes(orgId),
        });
      }
      groupMap.get(orgId)!.properties.push(property);
    }

    const groups = [...groupMap.values()];
    // Sort: groups with org access first, then alphabetically
    groups.sort((a, b) => {
      if (a.hasOrgAccess !== b.hasOrgAccess) return a.hasOrgAccess ? -1 : 1;
      return a.orgName.localeCompare(b.orgName);
    });
    return groups;
  }, [filteredProperties, orgNames, accessibleOrgIds]);

  // Determine if we should show grouped view
  const uniqueOrgIds = new Set(availableProperties.map(p => p.organizationId).filter(Boolean));
  const showGrouped = uniqueOrgIds.size > 1 || (uniqueOrgIds.size === 1 && (hasOrgRole || isSuperAdmin));

  // Hide if no properties, or if non-super-admin with only one property
  if (availableProperties.length === 0) return null;
  if (!isSuperAdmin && !hasOrgRole && availableProperties.length <= 1) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        type="button"
        variant="secondary"
        fullWidth
        onClick={() => setIsOpen(!isOpen)}
        className="!justify-start"
      >
        <Building2 className="w-4 h-4 mr-2 text-surface-500" />
        <span className="flex-1 text-left truncate">
          {selectedProperty ? selectedProperty.name : 'Select Property'}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-surface-200 rounded-lg shadow-lg">
          <div className="p-2">
            {/* Search Input */}
            <div className="mb-2">
              <Input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search properties..."
                inputSize="sm"
                leftIcon={<Search className="w-4 h-4" />}
              />
            </div>

            <div className="max-h-80 overflow-y-auto">
              {/* All Properties option -- super admins only */}
              {isSuperAdmin && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    fullWidth
                    onClick={() => {
                      setSelectedProperty(null, false);
                      navigate('/');
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    className={`!justify-start px-3 py-2 text-sm rounded-md font-normal ${
                      !selectedProperty ? '!bg-primary-50 !text-primary-700' : '!text-surface-700'
                    }`}
                  >
                    <Building2 className="w-4 h-4 mr-3 text-surface-400" />
                    <span className="flex-1 text-left">Admin Overview</span>
                    {!selectedProperty && <Check className="w-4 h-4 text-primary-600" />}
                  </Button>
                  <div className="my-2 border-t border-surface-200" />
                </>
              )}

              {filteredProperties.length === 0 ? (
                <div className="px-3 py-4 text-sm text-center text-surface-500">
                  No properties found
                </div>
              ) : showGrouped ? (
                // Grouped view
                orgGroups.map((group, groupIndex) => (
                  <div key={group.orgId}>
                    {groupIndex > 0 && <div className="my-1 border-t border-surface-100" />}

                    {/* Org header */}
                    <div className="px-3 py-1.5 text-xs font-semibold text-surface-500 uppercase tracking-wider">
                      {group.orgName}
                    </div>

                    {/* Org Overview option for users with org-level access */}
                    {(hasOrgRole || isSuperAdmin) && group.hasOrgAccess && group.orgId !== '__ungrouped__' && (
                      <Button
                        type="button"
                        variant="ghost"
                        fullWidth
                        onClick={() => {
                          setSelectedProperty(null, false);
                          navigate(`/organizations/${group.orgId}`);
                          setIsOpen(false);
                          setSearchQuery('');
                        }}
                        className="!justify-start px-3 py-2 text-sm rounded-md !text-surface-700 font-normal"
                      >
                        <LayoutDashboard className="w-4 h-4 mr-3 text-primary-500" />
                        <span className="flex-1 text-left font-medium text-primary-700">Organization Overview</span>
                      </Button>
                    )}

                    {/* Properties in this group */}
                    {group.properties.map(property => (
                      <PropertyItem
                        key={property.id}
                        property={property}
                        isSelected={selectedProperty?.id === property.id}
                        onSelect={() => {
                          setSelectedProperty(property, true);
                          setIsOpen(false);
                          setSearchQuery('');
                        }}
                        onManage={() => {
                          setSelectedProperty(property, false);
                          navigate('/properties');
                          setIsOpen(false);
                          setSearchQuery('');
                        }}
                      />
                    ))}
                  </div>
                ))
              ) : (
                // Flat view (single org or no org info)
                filteredProperties.map(property => (
                  <PropertyItem
                    key={property.id}
                    property={property}
                    isSelected={selectedProperty?.id === property.id}
                    onSelect={() => {
                      setSelectedProperty(property, true);
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    onManage={() => {
                      setSelectedProperty(property, false);
                      navigate('/properties');
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Property list item (extracted to reduce duplication)
// ---------------------------------------------------------------------------

function PropertyItem({
  property,
  isSelected,
  onSelect,
  onManage,
}: {
  property: Property;
  isSelected: boolean;
  onSelect: () => void;
  onManage: () => void;
}) {
  return (
    <div
      className={`flex items-center px-3 py-2 text-sm rounded-md group ${
        isSelected
          ? 'bg-primary-50 text-primary-700'
          : 'text-surface-700 hover:bg-surface-100'
      }`}
    >
      <Building2 className="w-4 h-4 mr-3 text-surface-400 flex-shrink-0" />
      <Button
        type="button"
        variant="ghost"
        onClick={onSelect}
        className="flex-1 min-w-0 !justify-start !text-current !p-0 !block text-left font-normal"
      >
        <div className="font-medium truncate">{property.name}</div>
        <div className="text-xs text-surface-500 truncate">{property.website}</div>
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        onClick={(e) => {
          e.stopPropagation();
          onManage();
        }}
        className="ml-2 p-1.5 rounded hover:bg-surface-200 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        title="Manage property"
        aria-label="Manage property"
      >
        <Settings className="w-3.5 h-3.5 text-surface-600" />
      </Button>
      {isSelected && (
        <Check className="w-4 h-4 ml-2 text-primary-600 flex-shrink-0" />
      )}
    </div>
  );
}
